import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Form, FormProps, Row, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FormElement from 'components/formElement/formElement';
import FileInput from 'components/fileInput/fileInput';

// services
import { onGetBrandIdentity, onUpdateBrandIdentity } from '../services/brandIdentityServices';
import NotificationService from 'core/services/notification';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { checkPhoneValidation } from 'utils/validators/phone';

interface BrandIdentityPageProps {}

const BrandIdentityPage: FunctionComponent<BrandIdentityPageProps> = () => {
	const { t } = useTranslation();
	const [brandIdentityForm] = Form.useForm();

	type FieldType = {
		company_logo: any;
		'company_name[ar]': string;
		'company_name[en]': string;
		'company_manager_name[ar]': string;
		'company_manager_name[en]': string;
		'description[ar]': string;
		'description[en]': string;
		contact_phone_country_code: string;
		contact_phone_number: string;
		contact_email: string;
	};

	const [phonenumber, setPhonenumber] = useState({ number: '', dialCode: '' });
	const [isUnderReview, setIsUnderReview] = useState<boolean>(false);
	const [logoSrc, setLogoSrc] = useState<string>('');

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useEffect(() => {
		getBrandIdentity();
	}, []);

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		brandIdentityForm.setFieldsValue({
			contact_phone_number: number,
			contact_phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(brandIdentityForm.getFieldValue('contact_phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.resolve();
	};

	const getBrandIdentity = async () => {
		try {
			setIsLoading(true);

			const response = await onGetBrandIdentity();

			setIsUnderReview(response.has_under_review_brand_identity_request);

			if (!response.has_under_review_brand_identity_request) {
				brandIdentityForm.setFieldsValue({
					'company_name[ar]': response.company_name.ar,
					'company_name[en]': response.company_name.en,
					'company_manager_name[ar]': response.company_manager_name.ar,
					'company_manager_name[en]': response.company_manager_name.en,
					'description[ar]': response.description.ar,
					'description[en]': response.description.en,
					company_logo: response.company_logo,
					contact_phone_country_code: response.contact_phone_country_code,
					contact_phone_number: response.contact_phone_number,
					contact_email: response.contact_email,
				});

				setLogoSrc(response.company_logo ?? '');
				setPhonenumber({ number: response.contact_phone_number!, dialCode: response.contact_phone_country_code! });
			}

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const updateBrandIdentity: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateBrandIdentityPayload = new FormData();
			updateBrandIdentityPayload.append('_method', 'put');

			updateBrandIdentityPayload.append('company_name[ar]', values['company_name[ar]']);
			updateBrandIdentityPayload.append('company_name[en]', values['company_name[en]']);
			updateBrandIdentityPayload.append('company_manager_name', values['company_manager_name[ar]']);
			updateBrandIdentityPayload.append('company_manager_name_en', values['company_manager_name[en]']);

			if (values['description[ar]']) updateBrandIdentityPayload.append('description[ar]', values['description[ar]']);

			if (values['description[en]']) updateBrandIdentityPayload.append('description[en]', values['description[en]']);

			if (values['contact_phone_country_code']) updateBrandIdentityPayload.append('contact_phone_country_code', values['contact_phone_country_code']);
			if (values['contact_phone_number']) updateBrandIdentityPayload.append('contact_phone_number', values['contact_phone_number']);

			if (values['contact_email']) updateBrandIdentityPayload.append('contact_email', values['contact_email']);

			if (typeof values['company_logo'] != 'string') updateBrandIdentityPayload.append('company_logo', values['company_logo']);

			await onUpdateBrandIdentity(updateBrandIdentityPayload);

			NotificationService.success(t('COMMON.SUCCESS'), t('COMPANY_INFORMATION.SUBMIT_IDENTITY_SUCCESS'));
			setIsUnderReview(true);
			setIsSubmitting(false);
		} catch (error: any) {
			setIsSubmitting(false);

			if (error.response.status == 422) mapResponseErrors(brandIdentityForm, error);

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			{isLoading ? (
				<LoadingComponent />
			) : isUnderReview ? (
				<Alert
					type="warning"
					message={<h6 className="fw-semibold">{t('COMPANY_INFORMATION.UNDER_REVIEW')}</h6>}
					description={t('COMPANY_INFORMATION.UNDER_REVIEW_DESC')}
				/>
			) : (
				<Card>
					<Row gutter={[16, 16]}>
						<Col span={24} lg={8}>
							<ComponentHeader title={t('COMPANY_INFORMATION.BRAND_IDENTITY')} subtitle={t('COMPANY_INFORMATION.UPDATE_BRAND_IDENTITY')} />
						</Col>
						<Col span={24} lg={16}>
							<Form form={brandIdentityForm} name="brandIdentityForm" onFinish={updateBrandIdentity}>
								<FormElement
									name="company_name[ar]"
									type="text"
									label={t('COMPANY_INFORMATION.COMPANY_AR_NAME')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
								/>

								<FormElement
									name="company_name[en]"
									type="text"
									label={t('COMPANY_INFORMATION.COMPANY_EN_NAME')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
								/>

								<FormElement
									name="company_manager_name[ar]"
									type="text"
									label={t('COMPANY_INFORMATION.COMPANY_MANAGER_AR_NAME')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
								/>

								<FormElement
									name="company_manager_name[en]"
									type="text"
									label={t('COMPANY_INFORMATION.COMPANY_MANAGER_EN_NAME')}
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
								/>

								<FormElement
									name="contact_email"
									label={t('COMPANY_INFORMATION.CONTACT_EMAIL')}
									type="email"
									rules={[{ type: 'email', message: t('COMMON.EMAIL_INVALID') }]}
								/>

								<Form.Item name="contact_phone_number" rules={[{ validator: validatePhone }]}>
									<PhoneInputComponent
										onChange={onChangePhone}
										label={t('COMPANY_INFORMATION.CONTACT_PHONE_NUMBER')}
										userPhoneNumber={phonenumber.number}
										userDialCode={phonenumber.dialCode}
									/>
								</Form.Item>
								<Form.Item name="contact_phone_country_code" hidden noStyle />

								<Typography.Text style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
									{t('COMPANY_INFORMATION.DESCRIPTION_IN_ARABIC')}
								</Typography.Text>
								<Form.Item name="description[ar]" rules={[{ validator: validateArabic }]}>
									<TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
								</Form.Item>

								<Typography.Text style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
									{t('COMPANY_INFORMATION.DESCRIPTION_IN_ENGLISH')}
								</Typography.Text>
								<Form.Item name="description[en]" rules={[{ validator: validateEnglish }]}>
									<TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
								</Form.Item>

								<Form.Item name="company_logo" style={{ marginTop: 20 }}>
									<FileInput
										accept="photo"
										name={t('COMPANY_INFORMATION.COMPANY_LOGO')}
										src={logoSrc}
										onSelect={(file: any) => brandIdentityForm.setFieldsValue({ company_logo: file })}
									/>
								</Form.Item>

								<Form.Item style={{ marginTop: 40 }}>
									<Button block type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
										{t('COMPANY_INFORMATION.SEND_REQUEST_FOR_APPROVAL')}
									</Button>
								</Form.Item>
							</Form>
						</Col>
					</Row>
				</Card>
			)}
		</>
	);
};

export default BrandIdentityPage;
